import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import TopBarRedesigned from 'components/TopBarRedesigned/TopBarRedesigned';
import Api from 'api';
import { LOCATION_IS_CENTRAL_BOOKED } from 'utils/constants';
import {
  PageContainer,
  Content,
  Inner,
  PaymentCardContainer,
  SectionTitleRow,
  SectionTitle,
  ReadOnlyBadge,
  NoteText,
  CardDisplay,
  CardBrandBox,
  CardNumberBlock,
  CardNumber,
  CardExpiry,
  StatusPill,
  TrustRow,
  TrustPill,
  InfoNote,
  PrimaryButton,
  ButtonRow,
  EditableSection,
  SecureLabel,
  PaymentElementBox,
  ErrorMessage,
  NoCardText,
} from './PaymentInfoPage.styles';
import CardBrandIcon from './CardBrand';
import { RefreshIcon, InfoIcon, ShieldCheckIcon, LightningIcon } from './icons';
import PaymentMethodSuccessModal from 'components/Modal/PaymentMethodSuccessModal/PaymentMethodSuccessModal';
import {
  COUNTRY_US,
  PAYMENT_PURPOSE_REVENUE,
  PaymentPurpose,
  getPaymentStripeCountry,
  isRevenuePurpose,
  isUSLocation,
} from 'utils/paymentPurpose';
import {
  PaymentPromptSourcePage,
  RevenueCardCheckpointOrigin,
  trackRevenueCardAddResult,
} from 'utils/paymentPromptAnalytics';

type LocationState = {
  locationId?: number;
  nextPage?: string;
  jobId?: number;
  offerId?: number;
  from?: string;
  paymentPurpose?: PaymentPurpose;
  sourcePage?: PaymentPromptSourcePage;
  checkpointOrigin?: RevenueCardCheckpointOrigin;
};

type StatusTone = 'active' | 'declined' | 'unknown';
export type CardAddFailureStage = 'setup_intent' | 'stripe_confirmation' | 'payment_update';
export type CardAddOutcome =
  | { succeeded: true }
  | { succeeded: false; failureStage: CardAddFailureStage; message?: string };

export const performCardAdd = async ({
  createSetupIntent,
  confirmCardSetup,
  updatePaymentInfo,
}: {
  createSetupIntent: () => Promise<any>;
  confirmCardSetup: (clientSecret: string | undefined) => Promise<{ error?: any }>;
  updatePaymentInfo: () => Promise<any>;
}): Promise<CardAddOutcome> => {
  let failureStage: CardAddFailureStage = 'setup_intent';
  try {
    const intent = await createSetupIntent();
    failureStage = 'stripe_confirmation';
    const { error } = await confirmCardSetup(intent?.client_secret);
    if (error) {
      return { succeeded: false, failureStage, message: error.message };
    }
    failureStage = 'payment_update';
    await updatePaymentInfo();
    return { succeeded: true };
  } catch (error: any) {
    return { succeeded: false, failureStage, message: error?.message };
  }
};

export const performCardRevalidation = async ({
  createSetupIntent,
  confirmCardSetup,
  handleConfirmationError,
  updatePaymentInfo,
}: {
  createSetupIntent: () => Promise<any>;
  confirmCardSetup: (clientSecret: string | undefined) => Promise<{ error?: any }>;
  handleConfirmationError: (error: any) => Promise<any>;
  updatePaymentInfo: () => Promise<any>;
}): Promise<CardAddOutcome> => {
  let failureStage: CardAddFailureStage = 'setup_intent';
  try {
    const intent = await createSetupIntent();
    failureStage = 'stripe_confirmation';
    const { error } = await confirmCardSetup(intent?.client_secret);
    if (error) {
      await handleConfirmationError(error);
      return { succeeded: false, failureStage, message: error.message };
    }
    failureStage = 'payment_update';
    await updatePaymentInfo();
    return { succeeded: true };
  } catch (error: any) {
    return { succeeded: false, failureStage, message: error?.message };
  }
};

const CORPORATE_NOTE_TEXT =
  'Note: The credit card on file is handled by 123 Corporate Office. Please contact them if you need to update your payment information.';

const getStatusDisplay = (isCardValid: number | undefined | null): { label: string; tone: StatusTone } => {
  switch (isCardValid) {
  case 1:
    return { label: 'Active', tone: 'active' };
  case 0:
    return { label: 'Declined', tone: 'declined' };
  case -1:
    return { label: 'Declined', tone: 'declined' };
  case -2:
    return { label: 'Expired', tone: 'declined' };
  default:
    return { label: 'Unknown', tone: 'unknown' };
  }
};

export const getPaymentInfoProceedAction = ({
  from,
  nextPage,
  jobId,
  locId,
  currentLocationId,
  offerId,
  sourcePage,
}: {
  from?: string;
  nextPage?: string;
  jobId?: number;
  locId?: number;
  currentLocationId?: number | null;
  offerId?: number;
  sourcePage?: PaymentPromptSourcePage;
}) => {
  if (from === 'dentist-job-partial') {
    return { type: 'replace', route: { pathname: 'dentistCustomOffers', state: { id: jobId } } } as const;
  }
  if (from === 'locationDetails') {
    return { type: 'replace', route: { pathname: 'locationDetails', state: { locationId: currentLocationId ?? locId } } } as const;
  }
  if (nextPage === 'custom-offers') {
    return { type: 'goReact', page: 'dentistCustomOffers', state: { id: jobId } } as const;
  }
  if (nextPage === 'jobPosted' && jobId) {
    return { type: 'push', route: { pathname: 'jobPosted', state: { id: jobId } } } as const;
  }
  if (nextPage === 'customOfferDetails' && offerId) {
    return {
      type: 'push',
      route: {
        pathname: 'customOfferDetails',
        state: { offerId, offerDetailsSourcePage: sourcePage || 'custom_offer_details' },
      },
    } as const;
  }
  return { type: 'push', route: { pathname: nextPage || 'dentistHome' } } as const;
};

export const getPaymentInfoBackAction = ({
  from,
  nextPage,
  jobId,
  locationId,
  currentLocationId,
  sourcePage,
}: {
  from?: string;
  nextPage?: string;
  jobId?: number;
  locationId?: number | null;
  currentLocationId?: number | null;
  sourcePage?: PaymentPromptSourcePage;
}) => {
  if (from === 'dentist-job-partial') {
    return { type: 'replace', route: { pathname: 'dentistCustomOffers', state: { id: jobId } } } as const;
  }
  if (from === 'locationDetails') {
    const id = currentLocationId ?? locationId;
    return id != null
      ? { type: 'replace', route: { pathname: 'locationDetails', state: { locationId: id } } } as const
      : { type: 'replace', route: { pathname: 'dentistLocations' } } as const;
  }
  if (from) {
    return { type: 'replace', route: { pathname: from } } as const;
  }
  if (nextPage === 'custom-offers') {
    return { type: 'goReact', page: 'dentistCustomOffers', state: { id: jobId } } as const;
  }
  if (nextPage === 'jobPosted' && jobId) {
    return { type: 'replace', route: { pathname: 'jobPosted', state: { id: jobId } } } as const;
  }
  if (nextPage === 'customOfferDetails') {
    if (sourcePage === 'custom_offers') {
      return { type: 'goReact', page: 'dentistCustomOffers', state: { id: jobId } } as const;
    }
    if (sourcePage === 'job_posted' && jobId) {
      return { type: 'replace', route: { pathname: 'jobPosted', state: { id: jobId } } } as const;
    }
    return { type: 'replace', route: { pathname: 'dentistHome' } } as const;
  }
  if (locationId != null) {
    return { type: 'replace', route: { pathname: 'locationDetails', state: { locationId } } } as const;
  }
  return { type: 'replace', route: { pathname: 'dentistLocations' } } as const;
};

const normalizePaymentStatus = (value: unknown): number | undefined => {
  if (value === undefined || value === null) return undefined;
  if (value === true) return 1;
  if (value === false) return 0;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? undefined : numeric;
};

const getPaymentCardValidity = (location: any, paymentPurpose?: PaymentPurpose) => {
  if (isRevenuePurpose(paymentPurpose) && isUSLocation(location)) {
    const sharedStatus = normalizePaymentStatus(location?.isCardValid);
    const revenueStatus = normalizePaymentStatus(location?.revenuePaymentInfoValid);
    if (sharedStatus === 1 && revenueStatus === 1) return 1;
    if (sharedStatus === -2 || revenueStatus === -2) return -2;
    if (sharedStatus === -1 || revenueStatus === -1) return -1;
    if (sharedStatus === 0 || revenueStatus === 0) return 0;
    return revenueStatus ?? sharedStatus;
  }
  return normalizePaymentStatus(location?.isCardValid);
};

const getStripePubKey = (location: any, paymentPurpose?: PaymentPurpose) => {
  const ts: any = (window as any).TempStars;
  const paymentCountry = getPaymentStripeCountry(location, paymentPurpose);
  if (paymentCountry === undefined || paymentCountry === null) {
    return ts?.StripePubKey;
  }
  const countryKey = Number(paymentCountry) === COUNTRY_US ? 'US' : 'CA';
  return ts?.Config?.stripe?.[countryKey]?.pubKey || ts?.StripePubKey;
};

const PaymentInfoPage: React.FC = () => {
  const history = useHistory();
  const { state } = useLocation<LocationState>();
  const [locationId, setLocationId] = useState<number | null>(state?.locationId ?? null);
  const paymentPurpose = state?.paymentPurpose || PAYMENT_PURPOSE_REVENUE;
  const cardAnalyticsSourcePage = state?.sourcePage || 'payment_info';
  const cardAnalyticsCheckpoint = state?.checkpointOrigin || 'direct_payment_info';
  const [stripe, setStripe] = useState<any>(null);
  const cardRef = useRef<any>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [nextPage] = useState<string | undefined>((history.location.state as any)?.nextPage);
  const [oldPaymentMethod, setOldPaymentMethod] = useState<any>(null);
  const [location, setLocation] = useState<any>(null);
  const [isValid, setIsValid] = useState<boolean>(false);
  const [successModal, setSuccessModal] = useState<{
    open: boolean;
    variant: 'saved' | 'revalidated';
    cardStatusLabel?: string;
    proceedLocationId?: number;
  }>({ open: false, variant: 'saved' });

  const is123D = location?.isCentralBooked === LOCATION_IS_CENTRAL_BOOKED._123_DENTIST;
  const is123DOwner = is123D && !!location?.isCorporatePaymentOwner;
  const is123DNonOwner = is123D && !location?.isCorporatePaymentOwner;
  const isEditable = !is123DNonOwner;

  const hasCard = !!oldPaymentMethod?.card;
  const card = oldPaymentMethod?.card;
  const cardBrand: string | null = card?.brand ?? null;
  const cardLast4 = card?.last4 || '';
  const cardExpMonth = card?.exp_month ? String(card.exp_month).padStart(2, '0') : '';
  const cardExpYear = card?.exp_year ? String(card.exp_year).slice(-2) : '';
  const statusDisplay = useMemo(
    () => (location ? getStatusDisplay(getPaymentCardValidity(location, paymentPurpose)) : null),
    [location, paymentPurpose]
  );

  useEffect(() => {
    (async () => {
      try {
        const ts: any = (window as any).TempStars;

        let locId = state?.locationId;
        if (!locId) {
          const paramLocId = (history.location.state as any)?.locationId;
          if (paramLocId) locId = paramLocId;
        }
        if (!locId && ts?.User?.getCurrentUser?.()?.locations?.[0]?.id) {
          locId = ts.User.getCurrentUser().locations[0].id;
        }
        if (locId) setLocationId(locId);

        const loc = locId ? await (Api as any).getLocationById(locId) : null;
        setLocation(loc);
        if (locId) {
          // Fetch default card silently: legacy tokens or missing Stripe customers
          // are not user-facing errors — they just mean "No Card on File".
          try {
            const pm = await (Api as any).getDefaultSource(locId, paymentPurpose);
            setOldPaymentMethod(pm);
          } catch {
            setOldPaymentMethod(null);
          }
        }
      } catch (e: any) {
        setMessage(e?.message || 'Failed to load payment info.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isEditable || !location) return;

    const init = async () => {
      try {
        const pubKey = getStripePubKey(location, paymentPurpose);
        if (!pubKey) return setMessage('Stripe is unavailable.');
        const stripeInstance = (window as any).Stripe?.(pubKey);
        if (!stripeInstance) return setMessage('Stripe is unavailable.');
        setStripe(stripeInstance);
        const elements = stripeInstance.elements();
        const cardEl = elements.create('card');
        cardRef.current = cardEl;
        cardEl.mount('#payment-element');
        cardEl.on('change', ({ error, complete }: any) => {
          setMessage(error ? error.message : null);
          setIsValid(!error && !!complete);
        });
      } catch (e: any) {
        setMessage(e?.message || 'Failed to initialize payments.');
      }
    };
    init();
    return () => {
      try { cardRef.current?.unmount?.(); } catch {}
      cardRef.current = null;
      setStripe(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditable, location, paymentPurpose]);

  const goBack = () => {
    const action = getPaymentInfoBackAction({
      from: state?.from,
      nextPage: state?.nextPage,
      jobId: state?.jobId,
      locationId: state?.locationId ?? locationId,
      currentLocationId: location?.id,
      sourcePage: state?.sourcePage,
    });

    if (action.type === 'goReact') {
      (window as any).TempStars?.App?.goReact?.(action.page, action.state);
      return;
    }
    history.replace(action.route);
  };

  const proceedNext = (locId: number) => {
    const ts: any = (window as any).TempStars;
    const from = (history.location.state as any)?.from;
    const action = getPaymentInfoProceedAction({
      from,
      nextPage,
      jobId: (history.location.state as any)?.jobId,
      locId,
      currentLocationId: location?.id,
      offerId: state?.offerId,
      sourcePage: state?.sourcePage,
    });

    if (action.type === 'replace') {
      history.replace(action.route);
      return;
    }
    if (action.type === 'goReact') {
      ts?.App?.goReact?.(action.page, action.state);
      return;
    }
    history.push(action.route);
  };

  const openSuccessModal = (
    variant: 'saved' | 'revalidated',
    locId: number,
    cardStatusLabel?: string,
  ) => {
    setSuccessModal({ open: true, variant, cardStatusLabel, proceedLocationId: locId });
  };

  const refreshCardAfterChange = async (locId: number) => {
    const refreshedLoc = await (Api as any).getLocationById(locId);
    setLocation(refreshedLoc);
    try {
      const pm = await (Api as any).getDefaultSource(locId, paymentPurpose);
      setOldPaymentMethod(pm);
    } catch {
      setOldPaymentMethod(null);
    }
    return refreshedLoc;
  };

  const revalidate = async () => {
    if (!locationId || !location) return;
    setIsLoading(true);
    setMessage(null);
    const ts: any = (window as any).TempStars;
    try {
      if (!oldPaymentMethod?.id) {
        setMessage('No existing card to re-validate.');
        setIsLoading(false);
        return;
      }
      const outcome = await performCardRevalidation({
        createSetupIntent: () => (Api as any).revalidateCard(locationId, paymentPurpose),
        confirmCardSetup: (clientSecret) => stripe.confirmCardSetup(clientSecret, {
          payment_method: oldPaymentMethod.id,
        }),
        handleConfirmationError: (error) => {
          const status = (ts?.Pages?.Dentist?.PaymentInfo?.getCardDeclineStatus
            && ts.Pages.Dentist.PaymentInfo.getCardDeclineStatus(error?.code)) || 0;
          return (Api as any).updateCardStatus({ locationId, isCardValid: status, paymentPurpose });
        },
        updatePaymentInfo: () => (Api as any).updatePaymentInfo(
          locationId,
          { isRevalidation: true, paymentPurpose },
        ),
      });

      if (!outcome.succeeded) {
        if (isRevenuePurpose(paymentPurpose) || cardAnalyticsCheckpoint === 'offer_details_view') {
          trackRevenueCardAddResult({
            result: 'failed',
            sourcePage: cardAnalyticsSourcePage,
            checkpointOrigin: cardAnalyticsCheckpoint,
            locationId,
            failureStage: outcome.failureStage,
            paymentPurpose,
            paymentRemediation: 'revalidate',
            jobId: state?.jobId,
            offerId: state?.offerId,
          });
        }
        setMessage(outcome.message || 'Re-validating error');
        setIsLoading(false);
        return;
      }

      if (isRevenuePurpose(paymentPurpose) || cardAnalyticsCheckpoint === 'offer_details_view') {
        trackRevenueCardAddResult({
          result: 'succeeded',
          sourcePage: cardAnalyticsSourcePage,
          checkpointOrigin: cardAnalyticsCheckpoint,
          locationId,
          paymentPurpose,
          paymentRemediation: 'revalidate',
          jobId: state?.jobId,
          offerId: state?.offerId,
        });
      }
      const refreshedLoc = await refreshCardAfterChange(locationId);
      await (window as any).TempStars?.User?.refresh?.();
      setIsLoading(false);

      const status = getStatusDisplay(getPaymentCardValidity(refreshedLoc, paymentPurpose));
      openSuccessModal('revalidated', locationId, status.label);
    } catch (e: any) {
      setMessage(e?.message || 'Re-validating error');
      setIsLoading(false);
    }
  };

  const save = async () => {
    if (!stripe || !cardRef.current || !locationId) return;
    if (!isValid) return;
    setIsLoading(true);
    setMessage(null);
    const outcome = await performCardAdd({
      createSetupIntent: () => (Api as any).createSetupIntent(locationId, { paymentPurpose }),
      confirmCardSetup: (clientSecret) => stripe.confirmCardSetup(clientSecret, {
        payment_method: { card: cardRef.current },
      }),
      updatePaymentInfo: () => (Api as any).updatePaymentInfo(locationId, { paymentPurpose }),
    });

    if (!outcome.succeeded) {
      if (isRevenuePurpose(paymentPurpose) || cardAnalyticsCheckpoint === 'offer_details_view') {
        trackRevenueCardAddResult({
          result: 'failed',
          sourcePage: cardAnalyticsSourcePage,
          checkpointOrigin: cardAnalyticsCheckpoint,
          locationId,
          failureStage: outcome.failureStage,
          paymentPurpose,
          paymentRemediation: 'add',
          jobId: state?.jobId,
          offerId: state?.offerId,
        });
      }
      setMessage(outcome.message || 'Failed to save payment info');
      setIsLoading(false);
      return;
    }

    (window as any).TempStars?.Analytics?.track?.('add payment info');
    if (isRevenuePurpose(paymentPurpose) || cardAnalyticsCheckpoint === 'offer_details_view') {
      trackRevenueCardAddResult({
        result: 'succeeded',
        sourcePage: cardAnalyticsSourcePage,
        checkpointOrigin: cardAnalyticsCheckpoint,
        locationId,
        paymentPurpose,
        paymentRemediation: 'add',
        jobId: state?.jobId,
        offerId: state?.offerId,
      });
    }

    try {
      await (window as any).TempStars?.User?.refresh?.();

      await refreshCardAfterChange(locationId);
      setIsLoading(false);

      openSuccessModal('saved', locationId);
    } catch (e: any) {
      // The provider and API update have already succeeded. A refresh failure
      // must not emit a contradictory card-add failure for the same attempt.
      setMessage('Payment information was saved, but the latest card details could not be refreshed.');
      setIsLoading(false);
      openSuccessModal('saved', locationId);
    }
  };

  const canRevalidate = hasCard && !isLoading && !!stripe;

  return (
    <PageContainer>
      <TopBarRedesigned onBack={goBack} title="Payment Method" />
      <Content>
        <Inner>
          <PaymentCardContainer>
            <SectionTitleRow>
              <SectionTitle>Re-Validate Your Card</SectionTitle>
              {is123DNonOwner && <ReadOnlyBadge>Read Only</ReadOnlyBadge>}
            </SectionTitleRow>

            {is123DNonOwner && <NoteText>{CORPORATE_NOTE_TEXT}</NoteText>}

            {hasCard ? (
              <CardDisplay>
                <CardBrandBox>
                  <CardBrandIcon brand={cardBrand} />
                </CardBrandBox>
                <CardNumberBlock>
                  <CardNumber>{`.... .... .... ${cardLast4}`}</CardNumber>
                  {cardExpMonth && cardExpYear && (
                    <CardExpiry>{`Expires ${cardExpMonth}/${cardExpYear}`}</CardExpiry>
                  )}
                </CardNumberBlock>
                {statusDisplay && (
                  <StatusPill tone={statusDisplay.tone}>{statusDisplay.label}</StatusPill>
                )}
              </CardDisplay>
            ) : (
              <NoCardText>No Card on File</NoCardText>
            )}

            <TrustRow>
              <TrustPill tone="mint">
                <ShieldCheckIcon />
                Secured by GeoTrust
              </TrustPill>
              <TrustPill tone="purple">
                <LightningIcon />
                Powered by Stripe
              </TrustPill>
            </TrustRow>

            {is123DNonOwner && (
              <InfoNote>
                <InfoIcon />
                <span>{CORPORATE_NOTE_TEXT}</span>
              </InfoNote>
            )}

            <ButtonRow>
              <PrimaryButton onClick={revalidate} disabled={!canRevalidate}>
                <RefreshIcon />
                {isLoading ? 'Re-Validating…' : 'Re-Validate Card'}
              </PrimaryButton>
            </ButtonRow>

            {isEditable && (
              <EditableSection>
                <SectionTitle>
                  {hasCard ? 'Update Payment Method' : 'Add Payment Method'}
                </SectionTitle>
                <SecureLabel>🔒 SECURE FORM</SecureLabel>
                <PaymentElementBox id="payment-element" />
                {message && <ErrorMessage>{message}</ErrorMessage>}
                <ButtonRow>
                  <PrimaryButton onClick={save} disabled={isLoading || !stripe || !isValid}>
                    {isLoading ? 'Saving…' : 'Save'}
                  </PrimaryButton>
                </ButtonRow>
              </EditableSection>
            )}

            {!isEditable && message && <ErrorMessage>{message}</ErrorMessage>}
          </PaymentCardContainer>
        </Inner>
      </Content>
      <PaymentMethodSuccessModal
        isOpen={successModal.open}
        variant={successModal.variant}
        cardStatusLabel={successModal.cardStatusLabel}
        onConfirm={() => {
          const locId = successModal.proceedLocationId;
          setSuccessModal((prev) => ({ ...prev, open: false }));
          if (locId != null) proceedNext(locId);
        }}
      />
    </PageContainer>
  );
};

export default PaymentInfoPage;
